import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API, formatGBP } from "@/lib/site";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", booking: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", booking: null });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const booking = await axios.get(`${API}/bookings/by-session/${sessionId}`);
          setState({ phase: "paid", booking: booking.data });
          return;
        }
      } catch (e) {
        // keep polling briefly — Stripe redirect can land before the webhook
      }
      attempts.current += 1;
      if (attempts.current < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "pending", booking: null });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const b = state.booking;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b101b] px-6 text-[#f1f5f9]" data-testid="payment-success-page">
      <div className="w-full max-w-lg rounded-3xl border border-[#38bdf8]/30 bg-gradient-to-b from-[#151e32] to-[#0d1424] p-8 text-center shadow-2xl">
        {state.phase === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-[#38bdf8]" />
            <h1 className="font-disp text-3xl uppercase text-white">Confirming Payment</h1>
            <p className="mt-2 text-sm text-slate-400">Securing your booking with our dispatch desk…</p>
          </>
        )}
        {state.phase === "paid" && b && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-400" />
            <h1 className="font-disp text-3xl uppercase text-white" data-testid="booking-confirmed-heading">Booking Confirmed</h1>
            <div className="mt-4 inline-block rounded-full border border-[#38bdf8]/40 bg-[#38bdf8]/10 px-4 py-1.5 font-mono-lbl text-sm tracking-widest text-[#38bdf8]" data-testid="booking-reference">
              {b.reference}
            </div>
            <div className="mt-6 space-y-2 rounded-2xl border border-white/10 bg-[#0b101b]/70 p-5 text-left text-sm" data-testid="booking-summary">
              <div className="flex justify-between"><span className="text-slate-400">Route</span><span className="text-white">{b.direction === "to_airport" ? `${b.pickup_zone} → ${b.airport_name}` : `${b.airport_name} → ${b.pickup_zone}`}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Pickup</span><span className="text-white">{b.date} · {b.time}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Vehicle</span><span className="text-white">{b.vehicle_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Passengers</span><span className="text-white">{b.passengers}</span></div>
              {b.flight_number && <div className="flex justify-between"><span className="text-slate-400">Flight</span><span className="text-white">{b.flight_number} (tracked)</span></div>}
              {b.return_journey && (
                <div className="flex justify-between" data-testid="booking-return-leg">
                  <span className="text-slate-400">Return</span>
                  <span className="text-white">
                    {b.direction === "to_airport" ? `${b.airport_name} → ${b.pickup_zone}` : `${b.pickup_zone} → ${b.airport_name}`}
                    {" · "}{b.return_date} · {b.return_time}
                  </span>
                </div>
              )}
              {b.return_journey && b.return_flight_number && <div className="flex justify-between"><span className="text-slate-400">Return flight</span><span className="text-white">{b.return_flight_number} (tracked)</span></div>}
              {b.discount_gbp > 0 && <div className="flex justify-between"><span className="text-slate-400">Round-trip saving</span><span className="text-emerald-400" data-testid="booking-discount">−{formatGBP(b.discount_gbp)}</span></div>}
              <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-slate-400">Paid</span><span className="font-bold text-[#38bdf8]" data-testid="booking-amount-paid">{formatGBP(b.amount_gbp)}</span></div>
            </div>
            <p className="mt-4 text-xs text-slate-400">Your voucher is on its way to {b.email}. Free cancellation up to 12h before pickup.</p>
            <p className="mt-2 text-xs text-slate-400">
              Questions about your pickup? Call us on{" "}
              <a href="tel:+447982651409" data-testid="confirmation-phone-link" className="font-bold text-[#38bdf8] hover:text-white">07982 651 409</a>
            </p>
          </>
        )}
        {(state.phase === "pending" || state.phase === "error") && (
          <>
            <h1 className="font-disp text-3xl uppercase text-white" data-testid="payment-pending-heading">Almost There</h1>
            <p className="mt-2 text-sm text-slate-400">
              {state.phase === "error"
                ? "We couldn't find that checkout session."
                : "Your payment is still being confirmed by the bank — check your email for the voucher shortly."}
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#38bdf8] px-6 py-3 text-xs font-bold uppercase tracking-wider text-[#0b101b] transition-colors hover:bg-white"
        >
          Book Another Transfer
        </Link>
      </div>
    </div>
  );
}
